import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import axios from 'src/utils/axios';
import Iconify from 'src/components/Iconify';
import Config from 'src/config';
import useAuth from 'src/hooks/useAuth';
import mktAccountsService from 'src/services/mktAccountsService';
import { Card, CardContent } from 'src/@/components/ui/card';
import {
    SESSION_TTL_MS,
    SUCCESS_EVENTS,
    isCodeExpired,
    recordCode,
    recordSuccessfulSession,
} from './embeddedSignupAttempt';

const FACEBOOK_SDK_VERSION = 'v25.0';
// So a origem interessa: o path do bridge e' montado aqui e o event.origin do
// postMessage nunca traz path. Um valor com path (ex.: .../webhook/v1/auth)
// geraria uma URL 404 e derrubaria a checagem de origem.
function toOrigin(value) {
    if (!value) return '';
    try {
        return new URL(value).origin;
    } catch (_) {
        return String(value).replace(/\/$/, '');
    }
}

const FACEBOOK_CONNECT_ORIGIN = toOrigin(import.meta.env.VITE_FACEBOOK_CONNECT_ORIGIN);

function persistClienteNumero(updateCliente, numero) {
    if (!numero) return;
    updateCliente({ numero });
    localStorage.setItem('numberId', numero);
}

function parseMessageData(data) {
    if (typeof data !== 'string') return data;

    try {
        return JSON.parse(data);
    } catch (_) {
        return null;
    }
}

function isFacebookOrigin(origin) {
    try {
        const { hostname, protocol } = new URL(origin);
        return protocol === 'https:' && (hostname === 'facebook.com' || hostname.endsWith('.facebook.com'));
    } catch (_) {
        return false;
    }
}

function getBridgeOrigins() {
    const origins = new Set([window.location.origin]);
    if (FACEBOOK_CONNECT_ORIGIN) origins.add(FACEBOOK_CONNECT_ORIGIN);
    return origins;
}

function getErrorMessage(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        error?.details?.message ||
        fallback
    );
}

// A mesma tela serve a dois destinos. O login na Meta e' identico; muda so
// onde o resultado e' persistido:
// - com channel_id: fluxo legado/reconexao, grava direto no canal
// - sem channel_id: fluxo de Contas Sociais, grava em lais_mkt_accounts e o
//   canal so recebe credenciais quando o usuario vincular
export default function FacebookConnectPage() {
    const { t } = useTranslation('settings');
    const navigate = useNavigate();
    const location = useLocation();
    const { user, updateCliente } = useAuth();
    const params = new URLSearchParams(location.search);
    const attemptRef = useRef(null);
    const popupRef = useRef(null);

    const appId = params.get('app_id');
    const configId = params.get('config_id');
    const channelId = params.get('channel_id');
    const modoContaSocial = !channelId;
    // return_to aceita so um nome de tela conhecido — nao uma URL livre, para
    // a pagina nao virar um redirecionador aberto.
    const DESTINOS = {
        'contas-sociais': '/dashboard/configuracoes/contas-sociais',
        canais: '/dashboard/configuracoes/canais',
    };
    const destinoRetorno = DESTINOS[params.get('return_to')]
        || (modoContaSocial ? DESTINOS['contas-sociais'] : DESTINOS.canais);

    const [accountId, setAccountId] = useState(null);
    const [status, setStatus] = useState('idle');
    const [signupMode, setSignupMode] = useState(null);
    const [registrationPin, setRegistrationPin] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [errorDebugInfo, setErrorDebugInfo] = useState(null);
    const [showDebugDetails, setShowDebugDetails] = useState(false);
    const [onboardingResult, setOnboardingResult] = useState(null);
    const [pinError, setPinError] = useState('');
    const [progressMessage, setProgressMessage] = useState(
        t('channels.facebookConnect.progressInitializing', 'Inicializando...')
    );

    const clearAttempt = useCallback(() => {
        if (attemptRef.current?.timeoutId) {
            clearTimeout(attemptRef.current.timeoutId);
        }
        attemptRef.current = null;
    }, []);

    const closeBridge = useCallback(() => {
        try {
            popupRef.current?.close();
        } catch (_) {
            // O navegador pode bloquear o fechamento de uma janela já encerrada.
        }
        popupRef.current = null;
    }, []);

    const failAttempt = useCallback(
        (message, debugInfo = null) => {
            clearAttempt();
            closeBridge();
            setErrorMessage(message);
            setErrorDebugInfo(debugInfo);
            setStatus('error');
        },
        [clearAttempt, closeBridge]
    );

    const cancelAttempt = useCallback(() => {
        clearAttempt();
        closeBridge();
        setStatus('idle');
        setProgressMessage(t('channels.facebookConnect.progressInitializing', 'Inicializando...'));
    }, [clearAttempt, closeBridge, t]);

    const submitAttempt = useCallback(async () => {
        const attempt = attemptRef.current;
        if (!attempt || attempt.submitted || !attempt.code || !attempt.sessionData) return;

        if (!attempt.codeReceivedAt || isCodeExpired(attempt)) {
            failAttempt(
                t(
                    'channels.facebookConnect.errorSessionExpired',
                    'A sessão de conexão expirou. Tente novamente.'
                )
            );
            return;
        }

        if (!modoContaSocial && !channelId) {
            failAttempt(
                t('channels.facebookConnect.errorChannelNotFound', 'Canal não encontrado.')
            );
            return;
        }
        if (modoContaSocial && !user?.clienteId) {
            failAttempt(
                t('channels.facebookConnect.errorClientNotFound', 'Cliente não identificado.')
            );
            return;
        }

        attempt.submitted = true;
        if (attempt.timeoutId) clearTimeout(attempt.timeoutId);
        setStatus('onboarding');
        setProgressMessage(
            modoContaSocial
                ? t(
                      'channels.facebookConnect.progressSavingAccount',
                      'Salvando a conta conectada...'
                  )
                : t(
                      'channels.facebookConnect.progressUpdatingChannel',
                      'Atualizando configurações do canal...'
                  )
        );

        try {
            const payload = {
                code: attempt.code,
                sessionData: attempt.sessionData,
                signupMode: attempt.signupMode,
            };
            const response = modoContaSocial
                ? await mktAccountsService.onboardWhatsapp(user.clienteId, payload)
                : (await axios.post(
                      `/api/v1/canais/${encodeURIComponent(channelId)}/whatsapp/onboard`,
                      payload
                  )).data;
            const result = response?.data || null;
            setOnboardingResult(result);
            if (result?.accountId) setAccountId(result.accountId);
            // No modo conta social o numero do cliente e' escrito no vinculo
            // (syncClientePhoneNumber): antecipar aqui criaria duas fontes.
            if (!modoContaSocial && result?.onboardingStatus === 'completed') {
                persistClienteNumero(updateCliente, result.numero);
            }
            attemptRef.current = null;
            closeBridge();
            setStatus(result?.onboardingStatus === 'awaiting_pin' ? 'awaiting_pin' : 'success');
        } catch (error) {
            failAttempt(
                getErrorMessage(
                    error,
                    t(
                        'channels.facebookConnect.errorConnectFailed',
                        'Erro ao conectar com o Facebook.'
                    )
                ),
                Config.dev ? error : null
            );
        }
    }, [channelId, closeBridge, failAttempt, modoContaSocial, t, updateCliente, user?.clienteId]);

    const receiveCode = useCallback(
        (code) => {
            const attempt = attemptRef.current;
            const firstCode = !attempt?.codeReceivedAt;
            if (!recordCode(attempt, code)) return;
            if (firstCode) {
                attempt.timeoutId = setTimeout(() => {
                    if (attemptRef.current === attempt && !attempt.submitted) {
                        failAttempt(
                            t(
                                'channels.facebookConnect.errorSessionExpired',
                                'A sessão de conexão expirou. Tente novamente.'
                            )
                        );
                    }
                }, SESSION_TTL_MS);
            }
            submitAttempt();
        },
        [failAttempt, submitAttempt, t]
    );

    const receiveSession = useCallback(
        (rawPayload) => {
            const attempt = attemptRef.current;
            if (!attempt || attempt.submitted) return;

            const payload = parseMessageData(rawPayload);
            if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

            if (isCodeExpired(attempt)) {
                failAttempt(
                    t(
                        'channels.facebookConnect.errorSessionExpired',
                        'A sessão de conexão expirou. Tente novamente.'
                    )
                );
                return;
            }

            if (SUCCESS_EVENTS.has(payload.event)) {
                recordSuccessfulSession(attempt, payload);
                submitAttempt();
                return;
            }

            if (payload.event === 'CANCEL') {
                cancelAttempt();
                return;
            }

            if (payload.event === 'ERROR') {
                failAttempt(
                    payload.data?.error_message ||
                        payload.data?.message ||
                        t(
                            'channels.facebookConnect.errorConnectFailed',
                            'Erro ao conectar com o Facebook.'
                        ),
                    Config.dev ? payload.data : null
                );
            }
        },
        [cancelAttempt, failAttempt, submitAttempt, t]
    );

    useEffect(() => {
        const currentAppId =
            appId || import.meta.env.VITE_FACEBOOK_APP_ID || '1553356899550775';
        const init = () => {
            window.FB.init({
                appId: currentAppId,
                version: FACEBOOK_SDK_VERSION,
                xfbml: false,
                cookie: false,
            });
        };

        if (window.FB) {
            init();
            return undefined;
        }

        window.fbAsyncInit = init;
        if (!document.getElementById('facebook-jssdk')) {
            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.async = true;
            script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
            document.head.appendChild(script);
        }

        return undefined;
    }, [appId]);

    useEffect(() => {
        const bridgeOrigins = getBridgeOrigins();

        const handleMessage = (event) => {
            const message = parseMessageData(event.data);
            if (!message) return;

            if (isFacebookOrigin(event.origin)) {
                receiveSession(message);
                return;
            }

            if (!bridgeOrigins.has(event.origin)) return;

            if (message.type === 'FB_CODE') {
                receiveCode(message.code);
                return;
            }

            if (message.type === 'FB_SESSION') {
                receiveSession(
                    message.sessionData ||
                        message.payload ||
                        (message.event
                            ? {
                                  type: 'WA_EMBEDDED_SIGNUP',
                                  event: message.event,
                                  data: message.data,
                              }
                            : message.data)
                );
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [receiveCode, receiveSession]);

    useEffect(
        () => () => {
            clearAttempt();
            closeBridge();
        },
        [clearAttempt, closeBridge]
    );

    const beginAttempt = useCallback(() => {
        clearAttempt();
        setErrorMessage('');
        setErrorDebugInfo(null);
        setOnboardingResult(null);
        setPinError('');
        setShowDebugDetails(false);
        setStatus('processing');
        setProgressMessage(
            t(
                'channels.facebookConnect.progressAwaitingAuthorization',
                'Aguardando autorização no Facebook...'
            )
        );

        const attempt = {
            code: null,
            codeReceivedAt: null,
            sessionData: null,
            signupMode,
            submitted: false,
            timeoutId: null,
        };
        attemptRef.current = attempt;

        return attempt;
    }, [clearAttempt, failAttempt, signupMode, t]);

    const runFbLogin = useCallback(() => {
        if (!signupMode || status !== 'idle') return;

        beginAttempt();
        const currentConfigId =
            configId || import.meta.env.VITE_FACEBOOK_CONFIG_ID || '4671161989780637';
        const needsBridge =
            FACEBOOK_CONNECT_ORIGIN &&
            FACEBOOK_CONNECT_ORIGIN !== window.location.origin;

        if (needsBridge) {
            const bridgeParams = new URLSearchParams({
                app_id:
                    appId ||
                    import.meta.env.VITE_FACEBOOK_APP_ID ||
                    '1553356899550775',
                config_id: currentConfigId,
                parent_origin: window.location.origin,
                signup_mode: signupMode,
                sdk_version: FACEBOOK_SDK_VERSION,
            });
            popupRef.current = window.open(
                `${FACEBOOK_CONNECT_ORIGIN}/oauth/facebook-whatsapp-bridge?${bridgeParams.toString()}`,
                'fb_whatsapp_connect',
                'width=640,height=720'
            );

            if (!popupRef.current) {
                failAttempt(
                    t(
                        'channels.facebookConnect.errorPopupBlocked',
                        'Popup bloqueado. Permita popups e tente novamente.'
                    )
                );
            }
            return;
        }

        if (!window.FB) {
            failAttempt(
                t(
                    'channels.facebookConnect.errorSdkLoading',
                    'Facebook SDK ainda está carregando. Aguarde um instante e tente novamente.'
                )
            );
            return;
        }

        const extras = { setup: {} };
        if (signupMode === 'coexistence') {
            extras.featureType = 'whatsapp_business_app_onboarding';
            extras.sessionInfoVersion = '3';
        }

        window.FB.login(
            (response) => {
                if (response?.authResponse?.code) {
                    receiveCode(response.authResponse.code);
                } else if (attemptRef.current) {
                    cancelAttempt();
                }
            },
            {
                config_id: currentConfigId,
                response_type: 'code',
                override_default_response_type: true,
                extras,
            }
        );
    }, [
        appId,
        beginAttempt,
        cancelAttempt,
        configId,
        failAttempt,
        receiveCode,
        signupMode,
        status,
        t,
    ]);

    const submitRegistrationPin = useCallback(async () => {
        const alvo = modoContaSocial ? accountId : channelId;
        if (!alvo || !/^\d{6}$/.test(registrationPin)) return;
        setPinError('');
        setStatus('onboarding');
        setProgressMessage(
            t(
                'channels.facebookConnect.progressRegisteringPhone',
                'Registrando o número no WhatsApp...'
            )
        );
        try {
            const response = modoContaSocial
                ? await mktAccountsService.registrarNumeroWhatsapp(alvo, registrationPin)
                : (await axios.post(
                      `/api/v1/canais/${encodeURIComponent(alvo)}/whatsapp/register`,
                      { pin: registrationPin }
                  )).data;
            const result = response?.data || null;
            setOnboardingResult(result);
            if (!modoContaSocial && result?.onboardingStatus === 'completed') {
                persistClienteNumero(updateCliente, result.numero);
            }
            setRegistrationPin('');
            setStatus('success');
        } catch (error) {
            setPinError(
                getErrorMessage(
                    error,
                    t(
                        'channels.facebookConnect.errorInvalidPin',
                        'Não foi possível registrar o número. Confira o PIN e tente novamente.'
                    )
                )
            );
            setStatus('awaiting_pin');
        }
    }, [accountId, channelId, modoContaSocial, registrationPin, t, updateCliente]);

    const isChoiceStep = status === 'idle' && !signupMode;
    const isConnectStep = status === 'idle' && signupMode;
    const isLoading = status === 'processing' || status === 'onboarding';
    const isAwaitingPhone = onboardingResult?.onboardingStatus === 'awaiting_phone';

    return (
        <div className="flex flex-col min-h-screen w-full items-center justify-center p-4 bg-gradient-to-b from-slate-50 to-slate-100/50 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Card className="w-full max-w-md shadow-xl border border-gray-100/80 rounded-2xl bg-white transition-all duration-500">
                <CardContent className="flex flex-col items-center justify-center p-8 gap-6 w-full">
                    <div className="relative">
                        <div className="absolute inset-0 bg-[#1877F2]/10 blur-2xl rounded-full scale-150" />
                        <div className="relative bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <Iconify icon="logos:facebook" width={40} height={40} />
                        </div>
                    </div>

                    <div className="text-center space-y-1.5">
                        <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                            {t(
                                'channels.facebookConnect.headerTitle',
                                'Conectar WhatsApp Business'
                            )}
                        </h2>
                        <p className="text-sm text-gray-400">
                            {t(
                                'channels.facebookConnect.headerSubtitle',
                                'Integração oficial via Meta Business'
                            )}
                        </p>
                    </div>

                    {isChoiceStep && (
                        <div className="w-full flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <p className="text-sm text-gray-500 text-center">
                                {t(
                                    'channels.facebookConnect.modeSelectionQuestion',
                                    'Como deseja conectar seu número?'
                                )}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setSignupMode('cloud_api')}
                                    className="p-4 rounded-xl border-2 border-gray-200 hover:border-[#7C3AED] hover:bg-violet-50/30 text-left transition-all duration-200 group"
                                >
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 group-hover:bg-violet-100 transition-colors mb-3">
                                        <Iconify
                                            icon="ic:round-cloud"
                                            width={22}
                                            className="text-blue-600 group-hover:text-violet-600 transition-colors"
                                        />
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 mb-1">
                                        Cloud API
                                    </p>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        {t(
                                            'channels.facebookConnect.cloudApiDescription',
                                            'Migra o número completamente para a Cloud API da Meta'
                                        )}
                                    </p>
                                    <div className="mt-3 flex items-center gap-1">
                                        <Iconify
                                            icon="eva:info-outline"
                                            width={12}
                                            className="text-gray-400"
                                        />
                                        <span className="text-[10px] text-gray-400">
                                            {t(
                                                'channels.facebookConnect.cloudApiHint',
                                                'Recomendado para números novos'
                                            )}
                                        </span>
                                    </div>
                                </button>

                                <button
                                    onClick={() => setSignupMode('coexistence')}
                                    className="p-4 rounded-xl border-2 border-gray-200 hover:border-[#7C3AED] hover:bg-violet-50/30 text-left transition-all duration-200 group"
                                >
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50 group-hover:bg-violet-100 transition-colors mb-3">
                                        <Iconify
                                            icon="eva:sync-outline"
                                            width={22}
                                            className="text-green-600 group-hover:text-violet-600 transition-colors"
                                        />
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 mb-1">
                                        {t(
                                            'channels.facebookConnect.coexistenceTitle',
                                            'Coexistência'
                                        )}
                                    </p>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        {t(
                                            'channels.facebookConnect.coexistenceDescription',
                                            'Mantém o WhatsApp Business App e a Cloud API simultâneos'
                                        )}
                                    </p>
                                    <div className="mt-3 flex items-center gap-1">
                                        <Iconify
                                            icon="eva:info-outline"
                                            width={12}
                                            className="text-gray-400"
                                        />
                                        <span className="text-[10px] text-gray-400">
                                            {t(
                                                'channels.facebookConnect.coexistenceHint',
                                                'Números já em uso no WA Business'
                                            )}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {isConnectStep && (
                        <div className="w-full flex flex-col items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100">
                                <Iconify
                                    icon={
                                        signupMode === 'cloud_api'
                                            ? 'ic:round-cloud'
                                            : 'eva:sync-outline'
                                    }
                                    width={14}
                                    className="text-violet-600"
                                />
                                <span className="text-xs font-medium text-violet-700">
                                    {signupMode === 'cloud_api'
                                        ? 'Cloud API'
                                        : t(
                                              'channels.facebookConnect.coexistenceTitle',
                                              'Coexistência'
                                          )}
                                </span>
                                <button
                                    onClick={() => setSignupMode(null)}
                                    className="ml-1 text-violet-400 hover:text-violet-600 transition-colors"
                                >
                                    <Iconify icon="eva:close-outline" width={14} />
                                </button>
                            </div>

                            <button
                                onClick={runFbLogin}
                                className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-[#1877F2] hover:bg-[#166FE5] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#1877F2]/20 active:transform active:scale-[0.98]"
                            >
                                <Iconify icon="logos:facebook" width={20} />
                                {t(
                                    'channels.facebookConnect.connectButton',
                                    'Conectar com Facebook'
                                )}
                            </button>

                            <button
                                onClick={() => setSignupMode(null)}
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {t('channels.facebookConnect.backButton', 'Voltar')}
                            </button>
                        </div>
                    )}

                    {isLoading && (
                        <div className="w-full flex flex-col items-center gap-5 p-6 rounded-xl bg-gradient-to-b from-gray-50 to-white border border-dashed border-gray-200">
                            <CircularProgress
                                size={36}
                                thickness={4}
                                sx={{ color: '#7C3AED' }}
                            />
                            <div className="text-center">
                                <p className="text-sm font-semibold text-gray-700 mb-1">
                                    {progressMessage}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {t(
                                        'channels.facebookConnect.progressWarning',
                                        'Por favor, não feche esta janela'
                                    )}
                                </p>
                            </div>
                            <div className="w-full max-w-[200px]">
                                <div className="flex items-center gap-1">
                                    <div className="h-1 flex-1 rounded-full bg-[#7C3AED]" />
                                    <div
                                        className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                            status === 'onboarding'
                                                ? 'bg-[#7C3AED]'
                                                : 'bg-gray-200'
                                        }`}
                                    />
                                </div>
                                <div className="flex justify-between mt-1.5">
                                    <span className="text-[9px] text-gray-400">
                                        {t('channels.facebookConnect.stepLogin', 'Login')}
                                    </span>
                                    <span className="text-[9px] text-gray-400">
                                        {t('channels.facebookConnect.stepSave', 'Salvar')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {status === 'awaiting_pin' && (
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                submitRegistrationPin();
                            }}
                            className="w-full flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
                        >
                            <div className="text-center space-y-1.5">
                                <div className="mx-auto h-14 w-14 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 border border-amber-100">
                                    <Iconify icon="eva:lock-outline" width={28} />
                                </div>
                                <h3 className="pt-2 text-lg font-bold text-gray-900">
                                    {onboardingResult?.pinMode === 'existing'
                                        ? t(
                                              'channels.facebookConnect.existingPinTitle',
                                              'Número já registrado'
                                          )
                                        : onboardingResult?.pinMode === 'new'
                                          ? t(
                                              'channels.facebookConnect.newPinTitle',
                                              'Proteja seu número'
                                            )
                                          : t(
                                                'channels.facebookConnect.pinTitle',
                                                'Confirme o PIN do número'
                                            )}
                                </h3>
                                <p className="text-sm text-gray-400">
                                    {onboardingResult?.pinMode === 'existing'
                                        ? t(
                                              'channels.facebookConnect.existingPinMessage',
                                              'Informe o PIN atual da verificação em duas etapas deste número.'
                                          )
                                        : onboardingResult?.pinMode === 'new'
                                          ? t(
                                                'channels.facebookConnect.newPinMessage',
                                                'Defina um PIN de 6 dígitos para registrar este número.'
                                            )
                                          : t(
                                                'channels.facebookConnect.pinMessage',
                                                'Informe o PIN atual ou defina um novo PIN de 6 dígitos.'
                                            )}
                                </p>
                            </div>
                            <input
                                type="password"
                                inputMode="numeric"
                                autoComplete="new-password"
                                maxLength={6}
                                autoFocus
                                value={registrationPin}
                                onChange={(event) => {
                                    setRegistrationPin(
                                        event.target.value.replace(/\D/g, '').slice(0, 6)
                                    );
                                    setPinError('');
                                }}
                                placeholder="••••••"
                                aria-label={t(
                                    'channels.facebookConnect.registrationPinLabel',
                                    'PIN de verificação em duas etapas'
                                )}
                                className="w-full px-4 py-3 border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 rounded-xl text-center tracking-[0.5em] font-mono outline-none"
                            />
                            {pinError && (
                                <p className="text-xs text-red-500 text-center">{pinError}</p>
                            )}
                            <button
                                type="submit"
                                disabled={!/^\d{6}$/.test(registrationPin)}
                                className="w-full px-4 py-3 bg-[#7C3AED] hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20"
                            >
                                {t(
                                    'channels.facebookConnect.registerPhoneButton',
                                    'Registrar número'
                                )}
                            </button>
                        </form>
                    )}

                    {status === 'success' && (
                        <div className="w-full flex flex-col items-center gap-5 animate-in zoom-in duration-300">
                            <div className="relative">
                                <div className={`absolute inset-0 ${isAwaitingPhone ? 'bg-amber-100' : 'bg-green-100'} blur-xl rounded-full scale-150`} />
                                <div className={`relative h-16 w-16 ${isAwaitingPhone ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-green-50 text-green-600 border-green-100'} rounded-2xl flex items-center justify-center border`}>
                                    <Iconify
                                        icon={isAwaitingPhone ? 'eva:clock-outline' : 'eva:checkmark-circle-2-fill'}
                                        width={32}
                                    />
                                </div>
                            </div>
                            <div className="text-center space-y-1.5">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {isAwaitingPhone
                                        ? t('channels.facebookConnect.awaitingPhoneTitle', 'Conta conectada, número pendente')
                                        : t('channels.facebookConnect.successTitle', 'Conexão realizada!')}
                                </h3>
                                <p className="text-sm text-gray-400 max-w-[260px] mx-auto">
                                    {isAwaitingPhone
                                        ? t(
                                              'channels.facebookConnect.awaitingPhoneMessage',
                                              'A WABA foi autorizada. Adicione um número para concluir a ativação.'
                                          )
                                        : modoContaSocial
                                          ? t(
                                                'channels.facebookConnect.successMessageAccount',
                                                'Conta conectada. Para começar a enviar e receber, vincule-a a um canal em Configurações → Canais.'
                                            )
                                          : t(
                                                'channels.facebookConnect.successMessage',
                                                'O WhatsApp foi conectado. O cliente ainda deve configurar o método de pagamento na Meta.'
                                            )}
                                </p>
                                {!isAwaitingPhone && onboardingResult?.numero && (
                                    <p className="text-sm font-medium text-gray-700">
                                        {t('channels.facebookConnect.syncedNumberLabel', 'Número sincronizado')}:{' '}
                                        <span className="font-mono">{onboardingResult.displayPhoneNumber || onboardingResult.numero}</span>
                                    </p>
                                )}
                            </div>

                            {!isAwaitingPhone && onboardingResult?.signupMode === 'coexistence' && (
                                <div className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left space-y-1.5">
                                    <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
                                        <Iconify icon="eva:sync-outline" width={14} />
                                        {t(
                                            'channels.facebookConnect.coexistenceSyncTitle',
                                            'Sincronizando dados do WhatsApp Business App'
                                        )}
                                    </p>
                                    <p className="text-xs text-amber-800 leading-relaxed">
                                        {t(
                                            'channels.facebookConnect.coexistenceSyncBody',
                                            'Estamos importando contatos e histórico. Mantenha o WhatsApp Business App aberto no celular — o processo pode levar alguns minutos.'
                                        )}
                                    </p>
                                    {modoContaSocial && onboardingResult?.coexistencePendingChannelLink && (
                                        <p className="text-xs text-amber-900 font-medium leading-relaxed">
                                            {t(
                                                'channels.facebookConnect.coexistenceLinkDeadline',
                                                'Vincule esta conta a um canal em até 24 horas para concluir a sincronização. Depois disso será necessário conectar novamente.'
                                            )}
                                            {onboardingResult?.syncDeadlineAt
                                                ? ` (${new Date(onboardingResult.syncDeadlineAt).toLocaleString('pt-BR')})`
                                                : ''}
                                        </p>
                                    )}
                                    {onboardingResult?.coexistenceSyncFailed && (
                                        <p className="text-xs text-red-700 leading-relaxed">
                                            {t(
                                                'channels.facebookConnect.coexistenceSyncFailed',
                                                'Não foi possível iniciar toda a sincronização agora. Tente vincular novamente ou contate o suporte se o problema persistir.'
                                            )}
                                        </p>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={() => navigate(destinoRetorno)}
                                className="w-full mt-1 px-4 py-3 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-[0.98]"
                            >
                                {modoContaSocial
                                    ? t(
                                          'channels.facebookConnect.backToAccountsButton',
                                          'Voltar para Contas Sociais'
                                      )
                                    : t(
                                          'channels.facebookConnect.backToChannelsButton',
                                          'Voltar para Canais'
                                      )}
                            </button>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="w-full flex flex-col items-center gap-4 animate-in zoom-in duration-300">
                            <div className="relative">
                                <div className="absolute inset-0 bg-red-100 blur-xl rounded-full scale-150" />
                                <div className="relative h-16 w-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 border border-red-100">
                                    <Iconify icon="eva:alert-circle-fill" width={32} />
                                </div>
                            </div>
                            <div className="text-center space-y-1.5">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {t(
                                        'channels.facebookConnect.errorTitle',
                                        'Falha na conexão'
                                    )}
                                </h3>
                                <p className="text-sm text-red-400 max-w-[300px] mx-auto leading-relaxed">
                                    {errorMessage}
                                </p>
                            </div>

                            {Config.dev && errorDebugInfo && (
                                <div className="w-full">
                                    <button
                                        onClick={() =>
                                            setShowDebugDetails(!showDebugDetails)
                                        }
                                        className="w-full text-left text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                                    >
                                        <Iconify
                                            icon={
                                                showDebugDetails
                                                    ? 'eva:chevron-down-outline'
                                                    : 'eva:chevron-right-outline'
                                            }
                                            width={14}
                                        />
                                        {t(
                                            'channels.facebookConnect.debugInfoToggle',
                                            'Debug info'
                                        )}
                                    </button>
                                    {showDebugDetails && (
                                        <pre className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-[10px] text-gray-600 overflow-auto max-h-[200px] whitespace-pre-wrap break-all">
                                            {JSON.stringify(errorDebugInfo, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col w-full gap-2 mt-1">
                                <button
                                    onClick={() => {
                                        setErrorMessage('');
                                        setErrorDebugInfo(null);
                                        setStatus('idle');
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors"
                                >
                                    {t(
                                        'channels.facebookConnect.retryButton',
                                        'Tentar Novamente'
                                    )}
                                </button>
                                <button
                                    onClick={() => navigate(destinoRetorno)}
                                    className="w-full px-4 py-2.5 text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                                >
                                    {t(
                                        'channels.facebookConnect.cancelButton',
                                        'Cancelar'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
